import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import VideoBox from "../../casinoComponents/videoBox";
import MyBet from "../../casinoComponents/myBet";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import UserSiteLastResult from "../../casinoComponents/userSiteLastResult";
import VirtualVideo from "../../casinoComponents/userSiteLastResult/VirtualVideo";
import VirtualRightSide from "../../casinoComponents/userSiteLastResult/VirtualRightSide";
import { RefCard } from "../../casinoComponents/userSiteLastResult/refBits";
import { joinSlots } from "../../casinoComponents/userSiteLastResult/refUtils";
import RefCardsOverlay from "../../casinoComponents/videoBox/overlays/RefCardsOverlay";
import DtlResultBody from "../../casinoComponents/refResultModal/DtlResultBody";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";
import { extractCasinoGame, isVirtualCasinoType } from "../../../utils/casinoGame";
import DtlLiveTable from "./DtlLiveTable";
import DtlVirtualTable from "./DtlVirtualTable";
import "../../../styles/games/btabledtl.css";

const WINNER_LEFT = `${import.meta.env.BASE_URL}img/winner-left.png`;

// Reference LastResultsSection (dtl20 / vdtl20): win 1 = D, 21 = T, 41 = L, 0 = tie
const dtlItem = (win) => ({
  className: win === "1" ? "resulta" : win === "21" ? "resultb" : win === "41" ? "resultc" : win === "0" ? "resulttie" : undefined,
  content: joinSlots(win, [[1, "D"], [21, "T"], [41, "L"], [0, "T"]]),
});

// Reference vdtl .vcasino-cards: Dragon / Tiger / Lion card (winner trophy when rdesc = 1 / 21 / 41)
function VdtlCards({ card, rdesc }) {
  const cards = String(card ?? "").split(",");
  const player = (label, cls, c, win) => (
    <div className="player-card playera-card">
      {String(rdesc) === win && <img src={WINNER_LEFT} className="v-winner" alt="" />}{" "}
      <div className={`w-100 ${cls} mb-2`}>{label}</div> <RefCard card={c} />
    </div>
  );
  return (
    <div className="vcasino-cards">
      {player("DRAGON", "playera", cards[0], "1")} {player("TIGER", "tie", cards[1], "21")}{" "}
      {player("LION", "playerb", cards[2], "41")}
    </div>
  );
}

// Live Market (/casino/dtl20) aur Live Virtual Market (/vcasino/dtl20 → vdtl20)
// dono isi page par. Pehle ye page sirf dummy tha: fake results, galat stream,
// bets/exposure kuch nahi, aur special bets hamesha locked.
export default function DragonTigerLion() {
  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "dtl20";
  const [gameData, setGameData] = useState({});
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const raw = gameData?.data?.data || {};
  const matchId = raw?.mid ? String(raw.mid) : "";

  useEffect(() => {
    let cancelled = false;
    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(gameName);
        if (!cancelled && response) setGameData(response);
      } catch (err) {
        console.error("Error fetching DTL game data:", err);
      }
    };
    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gameName]);

  const fetchExposure = useCallback(async () => {
    if (!matchId) return;
    try {
      const response = await getMatchExposure(null, matchId);
      if (response?.success && Array.isArray(response.data)) {
        const map = {};
        response.data.forEach((item) => {
          const name = String(item.team_name || "").trim();
          const amount = parseFloat(item.exposure_amount) || 0;
          if (name && amount !== 0) {
            map[name] = (map[name] || 0) + amount;
            map[name.toLowerCase()] = map[name];
          }
        });
        setExposures(map);
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        setMyBets(
          response.data.map((bet) => ({
            // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
            description: bet.description ?? bet.game_name ?? bet.gameName,
            userName: bet.username || "-",
            matchedBet: bet.selection || bet.player_name || "-",
            selection: bet.selection || "",
            odds: bet.odds ?? "0",
            stake: bet.amount ?? "0",
            placeDate: bet.created_at ?? "-",
            gametype: bet.game_type === "lay" ? "Lay" : "Back",
            type: bet.game_type || null,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    if (!matchId) return undefined;
    fetchExposure();
    const id = setInterval(fetchExposure, 2000);
    return () => clearInterval(id);
  }, [matchId, fetchExposure]);

  useEffect(() => {
    fetchMyBetsData();
    const id = setInterval(fetchMyBetsData, 3000);
    return () => clearInterval(id);
  }, [fetchMyBetsData]);

  const isVirtual = isVirtualCasinoType(gameName);
  // Reference titles (lastresults2 res1.cname)
  const title = isVirtual ? "V-20-20 D T L" : "20-20 D T L";
  const roundId = raw?.mid ?? "";
  const viewMoreModal = (
    <ViewMoreModal
      show={showViewMoreModal}
      onHide={() => setShowViewMoreModal(false)}
      title="View More"
      sectionKey={gameName}
    />
  );
  const lastResults = (
    <UserSiteLastResult gameType={gameName} renderItem={dtlItem} Body={DtlResultBody} fallbackTitle={title} />
  );

  // Live Virtual Market (/vcasino/dtl20): reference .casino-table.vcasino.vdtl
  if (isVirtual) {
    return (
      <CasinoTableLayout
        tableClass="vcasino vdtl"
        rightSide={<VirtualRightSide bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
      >
        <>
          <VirtualVideo
            gameType={gameName}
            title={title}
            data={raw}
            cardsNode={<VdtlCards card={raw?.card} rdesc={raw?.rdesc} />}
          />{" "}
          {roundId ? (
            <div className="casino-detail">
              <DtlVirtualTable raw={raw} exposures={exposures} gameType={gameName} />{" "}
              <div className="casino-video-last-results">{lastResults}</div>
            </div>
          ) : null}
        </>
        {viewMoreModal}
      </CasinoTableLayout>
    );
  }

  return (
    <CasinoTableLayout
      tableClass="dtl20"
      rightSide={<MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />}
    >
      <>
        <CasinoHeading name={title} roundId={roundId} />
        <VideoBox
          gameType={gameName}
          iframeSrc={`https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`}
          timerValue={raw?.lt}
          cardsOverlay={<RefCardsOverlay card={raw?.card} count={3} wrap />}
        />
        {roundId ? (
          <div className="casino-detail">
            <DtlLiveTable raw={raw} exposures={exposures} gameType={gameName} />{" "}
            <CasinoLastResult
              gameType={gameName}
              viewAllLink={`/admin/reports/casinoresult/${gameName}`}
              resultComponent={lastResults}
            />
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
